import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import {
  FARM_KEY_PREFIX,
  INPUT_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  INPUT_DOC_TAB_INSIGHTS,
  LINK_ALL_CROPS_WHOLE_FARM_LABEL,
  LIVE_TOTALS_FILTERED_EMPTY,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
  SUMMARY_INPUT_SUMMARIES_SECTION_ID,
} from 'constants/uiConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { InputTaskCategory } from 'interfaces/IInputTask';
import {
  SUMMARY_ENTITY_LABELS,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
} from 'constants/uiConstants';
import type { SummaryEntityCategory } from '../../../redux/slices/InsightsSlice';
import { QUANTITY_CATEGORY_ORDER } from 'pages/FarmsPage/Workspace/Insights/costCategories';
import {
  computeQuantityRequired,
  type QuantityRequiredLine,
} from 'pages/FarmsPage/Workspace/Insights/computeQuantityRequired';
import {
  computeWiderScopeQuantities,
  resolveSeason,
} from 'pages/FarmsPage/Workspace/Insights/computeScopeContext';
import {
  ACTUAL_ONLY_CATEGORIES,
  PLAN_ONLY_CATEGORIES,
  scaleForDisplay,
} from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import CircularQuantityChart, {
  type QuantityProgressItem,
} from './CircularQuantityChart';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import { filterInputsToStepCategory } from 'pages/FarmsPage/Workspace/Tasks/stepCategoryFilters';
import GridFilterNote, { useGridStepFilter } from './GridFilterNote';
import { SPEND_CATEGORY_COLORS } from './SpendPieChart';

/** One product's Plan and Actual quantity, merged by key so both categories share one row even when a product only appears in one of them. */
interface MergedQuantityRow {
  key: string;
  label: string;
  category: SummaryEntityCategory;
  unit: 'kg' | 'L';
  plan: number;
  actual: number;
}

const QuantityValue: React.FC<{
  quantity: number;
  unit: 'kg' | 'L';
  isOverPlan?: boolean;
}> = ({ quantity, unit, isOverPlan }) => {
  if (quantity <= 0) return <span className="text-gray-300">—</span>;
  const { value, unit: displayUnit } = scaleForDisplay(quantity, unit);
  return (
    <span className="whitespace-nowrap">
      <span className={isOverPlan ? 'font-semibold text-danger' : undefined}>
        {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>
      <span className="ml-1 text-sm text-gray-muted">
        {displayUnit}
      </span>
    </span>
  );
};

/**
 * The same product's total for the wider scope (OP-2392), shown under the
 * scoped quantity. What it covers is explained once, above the table.
 */
const WiderScopeCaption: React.FC<{
  quantity: number | undefined;
  unit: 'kg' | 'L';
}> = ({ quantity, unit }) => {
  if (!quantity || quantity <= 0) return null;
  const { value, unit: displayUnit } = scaleForDisplay(quantity, unit);
  return (
    <span className="block whitespace-nowrap text-sm text-gray-dark">
      {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
      {displayUnit}
    </span>
  );
};

/**
 * Right-panel tab for Quantity Required summary (OP-2273).
 *
 * Displays aggregated product quantities across all scoped inputs.
 * Application (Operation) steps are excluded — they carry a service cost
 * rather than a physical product quantity.
 *
 * Shows Plan and Actual side by side when the panel is wide enough; below
 * RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX it collapses to a single column driven
 * by the shared Plan/Actual control at the top of the Right Panel (OP-2333 —
 * one control for every section, not one per panel). The chart shows both at
 * once per product — Actual applied so far against Plan (a reference tick),
 * scaled against the largest Plan or Actual value in that product's half
 * (mass/liquid) — see CircularQuantityChart for the current design.
 *
 * Quantities are normalised to kg (mass) or L (liquid) and auto-scaled
 * to t for large values via scaleForDisplay.
 *
 * At plan or paddock scope, each quantity also shows the total for the same
 * season across the whole organisation, all crops (OP-2392) — e.g. total lime
 * to order.
 */
const QuantityRequiredPanel: React.FC = () => {
  const {
    scopedInputs,
    orgInputs,
    isLoading: isInputsLoading,
  } = useScopedInputs();
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const selectedFarmId = useSelector(
    (state: RootState) => state.farmReducer.selectedFarmId
  );
  const navigateToFarmsView = useFarmsLinkNavigate();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isWide = width === 0 || width >= RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;
  const narrowCategory = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
  );

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[RIGHT_PANEL_TAB_QUANTITY]
  );
  const { data: gUnits = [], isLoading: isGUnitsLoading } = useFetchGUnits(
    activeGOrganisation || null
  );

  const isLoading = isInputsLoading || isGUnitsLoading;

  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  // Follows the tasks grid's input filter (OP-2535).
  const gridFilter = useGridStepFilter();
  const filteredInputs = useMemo(
    () => filterInputsToStepCategory(scopedInputs, gridFilter.categoryKey),
    [scopedInputs, gridFilter.categoryKey]
  );

  const planResult = useMemo(
    () =>
      isLoading
        ? null
        : computeQuantityRequired(filteredInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [filteredInputs, gUnits, isLoading]
  );
  const actualResult = useMemo(
    () =>
      isLoading
        ? null
        : computeQuantityRequired(
            filteredInputs,
            gUnits,
            ACTUAL_ONLY_CATEGORIES
          ),
    [filteredInputs, gUnits, isLoading]
  );

  // Wider-scope caption: only below farm level (at farm/org scope the table
  // already is the wide view), and only when the scope is one season, so the
  // caption can name exactly what it covers. All crops are included —
  // products are ordered for the season, not per crop.
  const isNarrowScope =
    !!activeTreeNodeKey &&
    (activeTreeNodeKey.startsWith(INPUT_KEY_PREFIX) ||
      activeTreeNodeKey.startsWith(PADDOCK_KEY_PREFIX));
  const season = useMemo(
    () => (isNarrowScope ? resolveSeason(scopedInputs) : null),
    [isNarrowScope, scopedInputs]
  );
  const widerPlan = useMemo(
    () =>
      isLoading || season === null
        ? null
        : computeWiderScopeQuantities(
            orgInputs,
            season,
            gUnits,
            PLAN_ONLY_CATEGORIES
          ),
    [isLoading, season, orgInputs, gUnits]
  );
  const widerActual = useMemo(
    () =>
      isLoading || season === null
        ? null
        : computeWiderScopeQuantities(
            orgInputs,
            season,
            gUnits,
            ACTUAL_ONLY_CATEGORIES
          ),
    [isLoading, season, orgInputs, gUnits]
  );
  const showWiderScope = season !== null;

  const categoryRank = useMemo(
    () => new Map(QUANTITY_CATEGORY_ORDER.map((cat, i) => [cat, i])),
    []
  );

  const mergedRows = useMemo<MergedQuantityRow[]>(() => {
    const map = new Map<string, MergedQuantityRow>();
    const upsert = (line: QuantityRequiredLine, field: 'plan' | 'actual') => {
      const existing = map.get(line.key);
      if (existing) {
        existing[field] = line.quantity;
        return;
      }
      map.set(line.key, {
        key: line.key,
        label: line.label,
        // Seed dress is listed under Seed, as in Category spend (OP-2535).
        // Each product keeps its own row and unit, so nothing is added up.
        category:
          line.category === SUMMARY_ENTITY_SEED_DRESS
            ? SUMMARY_ENTITY_SEED
            : line.category,
        unit: line.unit,
        plan: field === 'plan' ? line.quantity : 0,
        actual: field === 'actual' ? line.quantity : 0,
      });
    };
    planResult?.lines.forEach((line) => upsert(line, 'plan'));
    actualResult?.lines.forEach((line) => upsert(line, 'actual'));

    return Array.from(map.values()).sort((a, b) => {
      const rankDiff =
        (categoryRank.get(a.category) ?? 0) - (categoryRank.get(b.category) ?? 0);
      if (rankDiff !== 0) return rankDiff;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
  }, [planResult, actualResult, categoryRank]);

  if (isLoading) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        <span className="text-base text-secondary-dark">Loading...</span>
      </div>
    );
  }

  const filterNote = gridFilter.label ? (
    <GridFilterNote label={gridFilter.label} onClear={gridFilter.clear} />
  ) : null;

  if (mergedRows.length === 0) {
    return (
      <div ref={ref} className="flex flex-col gap-3">
        {filterNote}
        <div className="flex items-center justify-center py-8 text-center">
          <span className="text-base text-secondary-dark">
            {filterNote ? LIVE_TOTALS_FILTERED_EMPTY : 'No quantity data'}
          </span>
        </div>
      </div>
    );
  }

  const isRowOverPlan = (row: MergedQuantityRow): boolean =>
    row.actual > 0 && (row.plan <= 0 || row.actual > row.plan);

  // Unique categories present in the data, preserving first-occurrence order.
  const legendEntries = mergedRows.reduce<
    { category: string; label: string }[]
  >((acc, row) => {
    if (!acc.some((e) => e.category === row.category)) {
      acc.push({
        category: row.category,
        label: SUMMARY_ENTITY_LABELS[row.category] ?? row.category,
      });
    }
    return acc;
  }, []);

  const chartItems: QuantityProgressItem[] = mergedRows.map((row) => ({
    key: row.key,
    label: row.label,
    category: row.category,
    unit: row.unit,
    plan: row.plan,
    actual: row.actual,
  }));

  return (
    <div ref={ref} className="flex flex-col gap-4">
      {filterNote}
      {showWiderScope && (
        <p className="text-sm text-gray-dark">
          Figure under each quantity: total quantity for all plans
        </p>
      )}
      {isNarrowScope && selectedFarmId && (
        <LinkButton
          onClick={() =>
            navigateToFarmsView({
              nodeKey: `${FARM_KEY_PREFIX}${selectedFarmId}`,
              tab: INPUT_DOC_TAB_INSIGHTS,
              hash: SUMMARY_INPUT_SUMMARIES_SECTION_ID,
            })
          }
        >
          {LINK_ALL_CROPS_WHOLE_FARM_LABEL}
        </LinkButton>
      )}
      <table className="w-full text-base">
        <thead>
          <tr className="border-b">
            <th className="pb-1.5 pr-3 text-left text-sm font-semibold text-secondary-dark">
              Input Name
            </th>
            {isWide ? (
              <>
                <th className="pb-1.5 text-right text-sm font-semibold text-secondary-dark">
                  Plan
                </th>
                <th className="pb-1.5 text-right text-sm font-semibold text-secondary-dark">
                  Actual
                </th>
              </>
            ) : (
              <th className="pb-1.5 text-right text-sm font-semibold text-secondary-dark">
                <Tooltip title="Excludes Operations">
                  <span className="cursor-help">Quantity Required</span>
                </Tooltip>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {mergedRows.map((row, idx) => (
            <tr
              key={row.key}
              onMouseEnter={() => setHoveredProduct(row.key)}
              onMouseLeave={() => setHoveredProduct(null)}
              className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <td className="py-1 pr-3 text-label text-charcoal">
                <span className="inline-flex items-center gap-1.5">
                  <svg width="8" height="8" aria-hidden="true" className="shrink-0">
                    <circle
                      cx="4"
                      cy="4"
                      r="3.5"
                      fill={SPEND_CATEGORY_COLORS[row.category] ?? '#9ca3af'}
                    />
                  </svg>
                  {row.label}
                </span>
              </td>
              {isWide ? (
                <>
                  <td className="py-1 text-right tabular-nums text-charcoal">
                    <QuantityValue quantity={row.plan} unit={row.unit} />
                    {showWiderScope && (
                      <WiderScopeCaption
                        quantity={widerPlan?.get(row.key)?.quantity}
                        unit={row.unit}
                      />
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums text-charcoal">
                    <QuantityValue
                      quantity={row.actual}
                      unit={row.unit}
                      isOverPlan={isRowOverPlan(row)}
                    />
                    {showWiderScope && (
                      <WiderScopeCaption
                        quantity={widerActual?.get(row.key)?.quantity}
                        unit={row.unit}
                      />
                    )}
                  </td>
                </>
              ) : (
                <td className="py-1 text-right tabular-nums text-charcoal">
                  <QuantityValue
                    quantity={
                      narrowCategory === InputTaskCategory.Plan
                        ? row.plan
                        : row.actual
                    }
                    unit={row.unit}
                    isOverPlan={
                      narrowCategory === InputTaskCategory.Actual && isRowOverPlan(row)
                    }
                  />
                  {showWiderScope && (
                    <WiderScopeCaption
                      quantity={
                        (narrowCategory === InputTaskCategory.Plan
                          ? widerPlan
                          : widerActual
                        )?.get(row.key)?.quantity
                      }
                      unit={row.unit}
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {chartVisible && (
        <CircularQuantityChart
          items={chartItems}
          hoveredKey={hoveredProduct}
          onHoverKeyChange={setHoveredProduct}
        />
      )}

      {/* ── Category legend — shown only when chart is visible ── */}
      {chartVisible && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-base text-secondary-dark">
          {legendEntries.map((e) => (
            <span key={e.category} className="flex items-center gap-1">
              <svg
                width="16"
                height="3"
                viewBox="0 0 16 3"
                aria-hidden="true"
                className="shrink-0"
              >
                <line
                  x1="0"
                  y1="1.5"
                  x2="16"
                  y2="1.5"
                  stroke={SPEND_CATEGORY_COLORS[e.category] ?? '#9ca3af'}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
              {e.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuantityRequiredPanel;
